import { ImportClient } from 'api/clients/ImportClient'
import { useDefaultProject } from 'hooks/queries/projects/useDefaultProject'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { isEqual } from 'lodash'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { forceFinderRefresh } from 'redux/actions'
import { AlmanacFile, RootState } from 'types'
import { HELP_CENTER_IMPORT_HELP_DOC, SUPPORT_EMAIL } from 'utils/constants'
import { getArticlePath } from 'utils/urlUtils'
import { CancelImport } from './CancelImport'
import Styles from './ImportModal.module.scss'
import { ImportingFiles } from './ImportingFiles'
import { Airtable, Canva, Confluence, Figma, GoogleDocs, GoogleSheets, GoogleSlides, Main, Notion } from './Sources'
import { FileImport, FileImportStatus, ImportStateType, SourceType } from './Types'
import { importStatusFrom, listenProcessing, title, uploadFile } from './utils'
import { Modal } from 'almanac-ui/Modal'
import { LocationPickerModalButton } from 'components/LocationPicker/LocationPickerModalButton'
import { useLocationPickerProps } from 'components/LocationPicker/hooks/useLocationPicker'
import { InlineLocationPicker } from 'components/InlineLocationPicker/InlineLocationPicker'
import { Title } from 'components/EditableModalTitle/EditableModalTitle'
import { IconButton } from 'components/IconButton/IconButton'
import { ReactComponent as BackIcon16 } from 'assets/icons/arrow--left_16.svg'

export interface ImportModalProps {
  defaultEmbedLink?: string
  defaultSource?: SourceType
  onGoBack?: () => void
  overrideFolder?: AlmanacFile | null
  visible?: boolean
  onClose?: () => void
  startReview?: boolean
}

const createFileImport = (file: File, index: number, src: string, status?: FileImportStatus): FileImport => {
  let source = src
  if (src === SourceType.GoogleDocs) source = 'docx'
  else if (source === SourceType.Main) source = file.name.split('.').pop() as string
  return {
    index,
    source,
    originalFile: file,
    articles: [],
    status: status || FileImportStatus.Initiated,
    abortController: new AbortController(),
  }
}

const ImportModal = ({
  visible,
  onClose,
  onGoBack,
  startReview,
  overrideFolder,
  defaultEmbedLink,
  defaultSource = SourceType.Main,
}: ImportModalProps) => {
  const websocketConnection = useWebsocketConnection()
  const [source, setSource] = useState<SourceType>(defaultSource)
  const [wideModal, setWideModal] = useState(false)
  const [importState, setImportState] = useState<ImportStateType>(ImportStateType.Import)
  const [fileImports, setFileImports] = useState<FileImport[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const auth = useSelector(({ auth }: RootState) => auth, isEqual)
  const { currentFolder } = useCurrentFolderStore()
  const dispatch = useDispatch()
  const workspaceId = useCurrentWorkspaceId()
  const history = useHistory()
  const defaultProjectQuery = useDefaultProject(workspaceId)
  const defaultProject = defaultProjectQuery?.data?.project || null
  const [chosenFolder, setChosenFolder] = useState(
    overrideFolder === undefined ? currentFolder || defaultProject : overrideFolder,
  )
  const [chosenWorkspaceId, setChosenWorkspaceId] = useState(workspaceId)

  const startImporting = useCallback(
    (files?: File[]) => {
      const imports = (files || selectedFiles).map((file, index) => createFileImport(file, index, source))
      setImportState(ImportStateType.Importing)
      setFileImports(imports)
    },
    [selectedFiles, source],
  )

  const onFilesSelected = useCallback(
    (files: File[]) => {
      setSelectedFiles(files)

      if (files.length > 0) {
        startImporting(files)
      }
    },
    [startImporting],
  )

  const close = useCallback(() => {
    if (importState !== ImportStateType.Importing) onClose?.()
    else {
      setImportState(ImportStateType.Cancelling)
    }
  }, [importState, onClose])

  const onConfirmClose = useCallback(
    (yes: boolean) => {
      setImportState(ImportStateType.Importing)
      if (yes) {
        fileImports.map((fileImport) => {
          if (fileImport.status === FileImportStatus.Uploading) {
            fileImport.abortController.abort()
          } else if (fileImport.status === FileImportStatus.Processing) {
            // No need to wait for the response
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ImportClient.cancelImport(fileImport.import_id!)
          }
          return fileImport
        })
        onClose?.()
      }
    },
    [fileImports, onClose],
  )

  const reset = useCallback(() => {
    setSelectedFiles([])
    setFileImports([])
    setImportState(ImportStateType.Import)
    setSource(defaultSource)
  }, [defaultSource])

  useEffect(() => {
    if (!fileImports.length) return
    if (!auth) return
    Promise.all(
      fileImports.map(async (fileImport: FileImport) => {
        if (fileImport.status !== FileImportStatus.Initiated) return
        setFileImports((prev) =>
          prev.map((item) =>
            item.index !== fileImport.index ? item : { ...item, status: FileImportStatus.Uploading },
          ),
        )
        try {
          const import_id = await uploadFile(fileImport, chosenWorkspaceId, chosenFolder?.id || null)
          setFileImports((prev) =>
            prev.map((item) =>
              item.index !== fileImport.index ? item : { ...item, import_id, status: FileImportStatus.Processing },
            ),
          )
          const importedFile = await listenProcessing(websocketConnection, import_id)
          setFileImports((prev) =>
            prev.map((item) =>
              item.import_id !== importedFile.id
                ? item
                : {
                    ...item,
                    import_id,
                    status: importStatusFrom(importedFile.status),
                    topLevelFolders: importedFile.top_level_folders,
                    articles: importedFile.articles,
                  },
            ),
          )
        } catch (errorMsg) {
          setFileImports((prev: FileImport[]) => {
            return prev.map((item) => {
              return item.index !== fileImport.index
                ? item
                : ({ ...item, errorMsg, status: FileImportStatus.Failed } as FileImport)
            })
          })
        }
      }),
    )
  }, [fileImports, auth, websocketConnection, chosenFolder, chosenWorkspaceId])

  useEffect(() => {
    setWideModal(
      [
        SourceType.Airtable,
        SourceType.Figma,
        SourceType.Canva,
        SourceType.GoogleSheets,
        SourceType.GoogleSlides,
      ].includes(source),
    )
  }, [source])

  useEffect(() => {
    // check whether all files are processed
    const allDone = fileImports.every(
      (item) =>
        item.status === FileImportStatus.Successful ||
        item.status === FileImportStatus.PartiallySuccessful ||
        item.status === FileImportStatus.Failed,
    )

    if (fileImports.length && allDone) {
      if (startReview) {
        history.push(`${getArticlePath({ slug: fileImports[0].articles[0].key })}/reviews/new`)
        onClose?.()
      } else {
        setImportState(ImportStateType.Imported)
      }
    }
  }, [fileImports, onClose, setImportState, history, startReview])

  useEffect(() => {
    if (importState === ImportStateType.Imported) dispatch(forceFinderRefresh())
  }, [dispatch, importState])

  useEffect(reset, [visible, reset])

  const Subtitle = () => {
    if (importState === ImportStateType.Importing) {
      return (
        <div className={Styles.subtitleV2}>
          Your files are being imported — please wait for the import to finish before dismissing this window.
        </div>
      )
    } else if (importState === ImportStateType.Imported) {
      const supportLink = (
        <a href={`mailto:${SUPPORT_EMAIL}`} target="_blank" rel="noreferrer">
          support
        </a>
      )
      const helpDoc = (
        <a href={HELP_CENTER_IMPORT_HELP_DOC} target="_blank" rel="noreferrer">
          help doc
        </a>
      )
      const succeededImports = fileImports.filter((f) => f.status === FileImportStatus.Successful).length
      return (
        <div className={Styles.subtitleV2}>
          Your import is done — {succeededImports} of {fileImports.length} files were successfully imported. If you need
          help importing docs, please refer to our {helpDoc}, or contact {supportLink}.
        </div>
      )
    } else {
      return <div />
    }
  }

  const renderBody = () => {
    if (importState === ImportStateType.Import) {
      switch (source) {
        case SourceType.Main:
          return <Main onSourceSelected={setSource} filesSelected={onFilesSelected} />
        case SourceType.Notion:
          return <Notion filesSelected={onFilesSelected} />
        case SourceType.Confluence:
          return <Confluence filesSelected={onFilesSelected} />
        case SourceType.GoogleDocs:
          return <GoogleDocs filesSelected={onFilesSelected} startReview={!!startReview} />
        case SourceType.Airtable:
          return (
            <Airtable
              onClose={close}
              startReview={!!startReview}
              parentId={chosenFolder?.project_id}
              parentSlug={chosenFolder?.handbook?.seo_string}
              defaultEmbedLink={defaultEmbedLink}
            />
          )
        case SourceType.Figma:
          return (
            <Figma
              onClose={close}
              startReview={!!startReview}
              parentId={chosenFolder?.project_id}
              parentSlug={chosenFolder?.handbook?.seo_string}
              defaultEmbedLink={defaultEmbedLink}
            />
          )
        case SourceType.Canva:
          return (
            <Canva
              onClose={close}
              startReview={!!startReview}
              parentId={chosenFolder?.project_id}
              parentSlug={chosenFolder?.handbook?.seo_string}
              defaultEmbedLink={defaultEmbedLink}
            />
          )
        case SourceType.GoogleSheets:
          return (
            <GoogleSheets
              onClose={close}
              startReview={!!startReview}
              parentId={chosenFolder?.project_id}
              parentSlug={chosenFolder?.handbook?.seo_string}
              defaultEmbedLink={defaultEmbedLink}
            />
          )
        case SourceType.GoogleSlides:
          return (
            <GoogleSlides
              onClose={close}
              startReview={!!startReview}
              parentId={chosenFolder?.project_id}
              parentSlug={chosenFolder?.handbook?.seo_string}
              defaultEmbedLink={defaultEmbedLink}
            />
          )
      }
    } else if (importState === ImportStateType.Cancelling) {
      return <CancelImport onClose={onConfirmClose} />
    } else {
      return (
        <ImportingFiles
          workspaceId={chosenWorkspaceId}
          fileImports={fileImports}
          onClose={close}
          importState={importState}
          folder={chosenFolder}
        />
      )
    }
  }

  const moveFolder = (newLocation: AlmanacFile) => {
    setChosenWorkspaceId(newLocation.real_workspace_id)
    setChosenFolder(newLocation)
  }

  const locationProps = useLocationPickerProps({ folder: chosenFolder, onSelectedFolder: moveFolder })
  const locationPicker = (
    <div className={Styles.locationPicker}>
      <span className={Styles.topLabel}>Create in</span>
      <InlineLocationPicker {...locationProps} />
    </div>
  )

  const goBack = () => {
    !!onGoBack && source === SourceType.Main ? onGoBack() : setSource(SourceType.Main)
  }

  return (
    <Modal.Root wrapperClassName={Styles.container} size={wideModal ? 'md' : 'sm'}>
      {importState === ImportStateType.Import && (
        <Modal.Top>
          <div className={Styles.topContainer}>
            {!!onGoBack && importState === ImportStateType.Import && source !== SourceType.Main && (
              <IconButton variant="tertiary" onPress={goBack} icon={BackIcon16} />
            )}
            {locationPicker}
          </div>
        </Modal.Top>
      )}
      <Modal.Header
        onClose={goBack}
        actions={
          importState === ImportStateType.Import && (
            <LocationPickerModalButton
              folderName={locationProps.buttonLabel}
              locationPickerProps={locationProps.locationPickerProps}
            />
          )
        }
      >
        <Title title={title(source, importState)} />
      </Modal.Header>
      <Subtitle />
      {renderBody()}
    </Modal.Root>
  )
}

export default ImportModal
